/**
 * Which tabs a project shows (design "Flow Project Tabs", boards 1, 1b, 1c, 2, 3, 7a, 7b): fewer by default, advanced
 * ones on demand.
 *
 * - The building tabs, always: Home · Apps · Preview. Code is the first of the advanced tabs, in More (pin it to keep it on the row). Then a divider and History (Changes, Activity and Sessions as
 *   three views of one tab, its count the Changes count). Always one tab: a `split` saved by an older version (the
 *   person's settings still accept it) is kept but never used.
 * - The advanced tabs, Code · Data · Configuration · API · MCP · Traces · Logs · Jobs · FinOps · Quality · Git · External Services · Terminal, are in More. Each has a mode, per person and the same in every
 *   project: 'more' (only in More), 'auto' (on the row by itself when the project needs it: Data once it has a
 *   datastore, Traces at 2 or more services or with a datastore (a trace shows the database calls), API once a service exposes routes, MCP once a service exposes an MCP server (`signals.mcpServers`,
 *   hooks/useMcpServersSignal.js), Quality while a check blocks Publish;
 *   "Smart tabs" off makes Auto the same as In More) or 'pinned' (always on the row). API, Data and Traces start in
 *   Auto, Quality, Configuration, External Services, Terminal and Git In More (Terminal and Git never show by
 *   themselves: they have no Auto reason). An auto tab arrives once: a dot and a one-time note. Quality carries a count (red): the
 *   release-gate rules that fail (`signals.qualityBlocked`), on the row and in More.
 * - A temporary tab: an advanced tab opened from More, a link (`?tab=traces`), the AI or the Publish dialog ("See in
 *   Quality") while it is not on the row sits
 *   at the end of the building tabs "for now", with Pin and Close, and goes when the project is left. It never pins.
 *
 * - The plan's hidden tabs (`plan.tabs.hidden` in the sandbox overview, hooks/usePlanFeatures.js): the sandbox admin
 *   can take tabs away from a plan (any but Home; History takes Changes, Activity and Sessions with it). They
 *   are gone from the row, More and Settings, and Pin, Auto, Hide and "Show advanced tabs" act on the visible ones
 *   only: the person's saved choice for a hidden tab is kept, unused, and back if the plan shows it again. Code hidden
 *   also takes Home's Files view and the links into the code ("Open in Code", a file link): the project Home opens
 *   instead (Canvas), Preview where a service is opened from Home and the plan shows it. The AI keeps reading and
 *   writing files: only the view is gone.
 * - Terminal is offered only where the person may open one (hooks/useTerminalTab.js: owner or editor, the owner's plan
 *   has the terminal): elsewhere it is passed in `hidden` like a tab the plan hides, whatever the person pinned.
 *
 * The preferences are the person's, kept by their sandbox (SettingsService, vfs-service `tabs`), so they follow them to
 * any browser: utils/tabPrefsSync.js reads and writes them there. This browser's localStorage (`flow.projectTabs`,
 * `flow.projectTabs.arrived`, `flow.history.view`) is the fast cache read first and the fallback when the sandbox is
 * not reachable; every change is written here first, then told to the sync (onTabPrefsStored).
 */
import { dataStoresOf, serviceRole } from './projectOverview';
import { isBrainPath } from './projectLayout';

export const BUILD_TABS = [
  { id: 'dashboard', label: 'Home', desc: 'Services, start and open the app' },
  { id: 'preview', label: 'Preview', desc: 'The running app' },
];

export const ADVANCED_TABS = [
  // Apps and Files start Pinned and lead the row, right after Home; a person can unpin them (they are then in More) and a
  // plan can hide them (`plan.tabs.hidden`).
  {
    id: 'apps',
    group: 'build',
    lead: true,
    label: 'Apps',
    desc: 'Each service: state, how it runs, configuration, API and quality.',
    short: 'Each service in one page',
    rule: 'Apps stays pinned',
  },
  {
    id: 'files',
    group: 'build',
    lead: true,
    label: 'Files',
    desc: 'Inputs, results and the Brain, in one table.',
    short: 'Inputs, results and the Brain',
    rule: 'Files stays pinned',
  },
  {
    // The first of More: the project's files and the editor. The AI reads and writes files whatever the tabs (hiding
    // Code, in the plan or by the person, only takes the visual editor away).
    id: 'code',
    group: 'build',
    label: 'Code',
    desc: 'Files and editor.',
    short: 'Files and editor',
    rule: 'Code stays in More',
  },
  {
    id: 'data',
    group: 'build',
    label: 'Data',
    desc: 'Browse and edit the records in your datastores.',
    short: 'Records in your datastores',
    rule: 'Data, once the project has a datastore',
  },
  {
    // In Build since 2026-10-29, after Data: setting a service's variables and secrets is part of making it run.
    id: 'config',
    group: 'build',
    label: 'Configuration',
    desc: 'Environment variables and secrets of each service.',
    short: 'Variables and secrets',
    rule: 'Configuration, while a service has a secret still to set',
  },
  {
    id: 'api',
    group: 'verify',
    label: 'API',
    desc: 'The endpoints your services expose; send a test request.',
    short: 'Endpoints; send a test request',
    rule: 'API, once a service exposes routes',
  },
  {
    id: 'mcp',
    group: 'verify',
    label: 'MCP',
    desc: 'The MCP servers your services expose; list and call their tools.',
    short: 'MCP servers; call their tools',
    rule: 'MCP, once a service exposes an MCP server',
  },
  {
    id: 'traces',
    group: 'operate',
    label: 'Traces',
    desc: 'Follow one request across your services, step by step, with timings.',
    short: 'One request across services',
    rule: 'Traces, once there are 2 or more services or a datastore',
  },
  {
    // Each service's build and running output (the project Home's Logs view, sandbox-first only: elsewhere it is passed
    // in `hidden`, hooks/useProjectTabs.js). A tab like the others: in Operate, pinnable, never by itself.
    id: 'logs',
    group: 'operate',
    label: 'Logs',
    desc: 'Each service\'s build and running output.',
    short: 'Build and run output per service',
    rule: 'Logs stays in More',
  },
  {
    // Every service's scheduled jobs in one list (components/appconfig/JobsPanel). It was Configuration opened on its
    // jobs: `{ tab: 'config', focus: { section: 'jobs' } }` still lands here (workspaceModel normalizeView). The plan
    // hides it together with Configuration (hiddenTabIds).
    id: 'jobs',
    group: 'operate',
    label: 'Jobs',
    desc: 'The scheduled jobs of each service: when they run, Run now and their runs.',
    short: 'Scheduled jobs; Run now, runs',
    rule: 'Jobs stays in More',
  },
  {
    // What the project costs (components/finops/FinOpsPanel): in Flow (credits, from what runs now), for AI (AI Foundry:
    // coming) and in production (an estimate, from vfs-service's FinOps model). A plan may hide it (vfs-service TAB_IDS);
    // hiding it also hides the vfs_finops AI tool.
    id: 'finops',
    group: 'operate',
    label: 'FinOps',
    desc: 'What this project costs: in Flow, for AI, and in production.',
    short: 'Costs in Flow, AI and production',
    rule: 'FinOps stays in More',
  },
  {
    id: 'quality',
    group: 'verify',
    label: 'Quality',
    desc: 'Tests, lint, vulnerabilities, secrets and licences; what blocks Publish.',
    short: 'Tests, security; what blocks Publish',
    rule: 'Quality, while a check blocks Publish',
  },
  {
    // Every service's git repo (components/git/GitTab): owners and editors commit, tag, push and pull; viewers look.
    id: 'git',
    group: 'build',
    label: 'Git',
    desc: 'Each service\'s repository: commit, tag, branch, push and pull; clone it from Flow.',
    short: 'Commit, push, pull; clone from Flow',
    rule: 'Git stays in More',
  },
  {
    // The id stays `mail` (the tab was Mail before it grew into External Services): pins, `?tab=mail` links and the
    // server's saved preferences keep opening it.
    id: 'mail',
    group: 'build',
    label: 'External Services',
    desc: 'Systems your app talks to: Mail today; CRMs, messaging systems and external APIs coming soon.',
    short: 'Mail today; CRMs, messaging, APIs soon',
    rule: 'External Services stays in More',
  },
  {
    // Only for the project's owner and editors, where the plan has the terminal (hooks/useTerminalTab.js).
    id: 'terminal',
    group: 'operate',
    label: 'Terminal',
    desc: 'A shell in your sandbox, in the project\'s folder; several at once, each in its own tab.',
    short: 'A shell in your sandbox',
    rule: 'Terminal stays in More',
  },
];
/**
 * The advanced tabs are grouped by the step of the work they serve (design "Flow Project Redesign", Tabs board), the
 * order More lists them in: Build, Verify, Ship, Operate. Home, Preview and History stay on the row.
 */
export const TAB_GROUPS = [
  { id: 'build', label: 'Build', hint: 'Write, configure and run the app' },
  { id: 'verify', label: 'Verify', hint: 'Check it works' },
  { id: 'ship', label: 'Ship', hint: 'Publish and deploy' },
  { id: 'operate', label: 'Operate', hint: 'Watch it run' },
];
/**
 * Team, after Operate: no tab of its own, only actions (Share, Messages, Comments, Suggestions, Notes) the header gives
 * (UnifiedTopBar `groupActions`); shown only when it has one.
 */
export const TEAM_GROUP = { id: 'team', label: 'Team', hint: 'Share and work together' };

/** `items` (anything with a tab `group`) split by group, in TAB_GROUPS order, empty groups left out. */
export function groupTabs(items = []) {
  return TAB_GROUPS
    .map((group) => ({ ...group, items: items.filter((item) => item.group === group.id) }))
    .filter((group) => group.items.length > 0);
}

/** The title on top of a tab's page: its step of the work (`group`), its name and what it is for; null for a tab with none. */
export function tabHeading(id) {
  if (id === 'history' || HISTORY_IDS_FOR_HEADING.includes(id)) {
    return { group: 'build', groupLabel: 'Build', title: 'History', desc: 'Changes, Activity and Sessions of the project.' };
  }
  if (id === 'files') { return { group: null, groupLabel: null, title: 'Files', desc: 'Inputs, results and the project\'s Brain in one table.' }; }
  if (id === 'apps') { return { group: null, groupLabel: null, title: 'Apps', desc: 'Each service: state, how it runs, configuration, API and quality.' }; }
  if (id === 'preview') { return { group: 'build', groupLabel: 'Build', title: 'Preview', desc: 'Run the app and open its link.' }; }
  const tab = ADVANCED_TABS.find((item) => item.id === id);
  if (!tab) { return null; }
  return { group: tab.group, groupLabel: TAB_GROUPS.find((group) => group.id === tab.group)?.label || null, title: tab.label, desc: tab.desc };
}
const HISTORY_IDS_FOR_HEADING = ['changes', 'activity', 'sessions'];

export const ADVANCED_IDS = ADVANCED_TABS.map((tab) => tab.id);

export const HISTORY_SUBS = [
  { id: 'changes', label: 'Changes' },
  { id: 'activity', label: 'Activity' },
  { id: 'sessions', label: 'Sessions' },
];
export const HISTORY_IDS = HISTORY_SUBS.map((sub) => sub.id);

export const TAB_MODES = [
  { id: 'more', label: 'In More' },
  { id: 'auto', label: 'Auto' },
  { id: 'pinned', label: 'Pinned' },
];

export const DEFAULT_TAB_PREFS = Object.freeze({
  modes: Object.freeze({ apps: 'pinned', files: 'pinned', code: 'more', api: 'auto', mcp: 'auto', data: 'auto', traces: 'auto', logs: 'more', jobs: 'more', finops: 'more', quality: 'more', config: 'more', mail: 'more', terminal: 'more', git: 'more' }),
  smart: true,
  split: false,
});

const PREFS_KEY = 'flow.projectTabs';
const ARRIVED_KEY = 'flow.projectTabs.arrived';
const HISTORY_KEY = 'flow.history.view';
export const TAB_PREFS_EVENT = 'flow:project-tabs-changed';

const isMode = (mode) => TAB_MODES.some((m) => m.id === mode);

// Told after each change written to this browser (the server sync, utils/tabPrefsSync.js). One listener.
let storedListener = null;
export function onTabPrefsStored(listener) { storedListener = listener; }
function stored() {
  if (!storedListener) { return; }
  try { storedListener(); } catch { /* the change stays in this browser */ }
}

/** Any stored value → full preferences, defaults for what is missing or wrong. */
export function normalizeTabPrefs(raw) {
  const value = raw && typeof raw === 'object' ? raw : {};
  const modes = {};
  ADVANCED_IDS.forEach((id) => { modes[id] = isMode(value.modes?.[id]) ? value.modes[id] : DEFAULT_TAB_PREFS.modes[id]; });
  return {
    modes,
    smart: typeof value.smart === 'boolean' ? value.smart : DEFAULT_TAB_PREFS.smart,
    // Kept as stored (older versions and the server's settings have it), never used: History is always one tab.
    split: typeof value.split === 'boolean' ? value.split : DEFAULT_TAB_PREFS.split,
  };
}

export function readTabPrefs() {
  try {
    return normalizeTabPrefs(JSON.parse(localStorage.getItem(PREFS_KEY) || 'null'));
  } catch {
    return normalizeTabPrefs(null);
  }
}

/**
 * Saves the preferences in this browser and tells every tab row (TAB_PREFS_EVENT) and the server sync. Resolves to
 * what was saved. `silent`: the server's own values being cached, nothing to send back.
 */
export function saveTabPrefs(prefs, { silent = false } = {}) {
  const next = normalizeTabPrefs(prefs);
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* kept for this page only */ }
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(TAB_PREFS_EVENT, { detail: next })); }
  if (!silent) { stored(); }
  return next;
}

// ---- the plan's hidden tabs -----------------------------------------------------------------------------------------

/** The tab a plan can never hide: Home (where a hidden tab's links land). */
export const FIXED_TAB_IDS = ['dashboard'];

/**
 * The plan's hidden tabs as a list of every id they take away: Home never, History with Changes, Activity
 * and Sessions. Anything else (unknown ids, a tab this build does not have yet) is kept: it hides nothing.
 */
export function hiddenTabIds(hidden) {
  const list = Array.isArray(hidden) ? hidden.filter((id) => typeof id === 'string' && !FIXED_TAB_IDS.includes(id)) : [];
  const out = list.includes('history') ? [...new Set([...list, ...HISTORY_IDS])] : list;
  // Jobs was part of Configuration and vfs-service's plans do not know a `jobs` tab yet (admin/plans.js TAB_IDS, where
  // the scheduled jobs' AI tools vfs_jobs_* belong to `config`): a plan that hides Configuration hides Jobs too.
  return out.includes('config') && !out.includes('jobs') ? [...out, 'jobs'] : out;
}

/** Whether the plan hides the tab (or the view: `changes` with History, `home` never). */
export const isTabHidden = (hidden, id) => Boolean(id) && hiddenTabIds(hidden).includes(id);

/**
 * The short note when a link, the AI or an old bookmark opens a tab the plan hides (the project's Home opens instead):
 * "Traces is not in your plan, so here is the project Home." History's views are named History.
 */
export function hiddenTabNote(id) {
  // The plan, the person's role or the owner's plan: the Terminal is not theirs here, whichever it is.
  if (id === 'terminal') { return 'The terminal is not available to you in this project, so here is the project Home.'; }
  const tab = [...BUILD_TABS, ...ADVANCED_TABS].find((t) => t.id === id);
  const label = HISTORY_IDS.includes(id) || id === 'history' ? 'History' : tab?.label || 'This tab';
  return `${label} is not in your plan, so here is the project Home.`;
}

/**
 * Where the Code gate (Canvas) sends a view the plan's hidden Code leaves with nothing to show, or null to stay:
 * `{ go: 'home' }` from a service on its Code view (or on Preview, Preview hidden too), `{ go: 'dashboard' }` from
 * Home's Files view. `note`: the person asked for Code (say why they are on Home), not only for a hidden Preview.
 * `resource` is the selected resource's type ('service' or 'configuration', Home), `homeMode` Home's view.
 */
export function codeGate({ hidden = [], resource = null, activeTab = null, homeMode = 'dashboard' } = {}) {
  if (!isTabHidden(hidden, 'code')) { return null; }
  if (resource === 'service') {
    if (activeTab === 'code') { return { go: 'home', note: true }; }
    return activeTab === 'preview' && isTabHidden(hidden, 'preview') ? { go: 'home', note: false } : null;
  }
  return resource === 'configuration' && homeMode === 'files' ? { go: 'dashboard', note: true } : null;
}

/** The advanced tabs the plan shows. */
export const visibleAdvanced = (hidden = []) => ADVANCED_TABS.filter((tab) => !isTabHidden(hidden, tab.id));

/** Whether every advanced tab the plan shows is pinned ("Show advanced tabs"). */
export const allPinned = (prefs, hidden = []) => {
  const visible = visibleAdvanced(hidden);
  return visible.length > 0 && visible.every((tab) => prefs.modes[tab.id] === 'pinned');
};

/**
 * "Show advanced tabs" on: all pinned; off: all back to Auto, the ones that start In More (Quality, Configuration,
 * External Services, Terminal, Git) back there. Only the tabs the plan shows: a hidden one keeps what the person chose for it.
 */
export const withAllPinned = (prefs, on, hidden = []) => ({
  ...prefs,
  modes: Object.fromEntries(ADVANCED_IDS.map((id) => [id, isTabHidden(hidden, id)
    ? prefs.modes[id]
    : on ? 'pinned' : DEFAULT_TAB_PREFS.modes[id] === 'auto' ? 'auto' : DEFAULT_TAB_PREFS.modes[id]])),
});

export const withMode = (prefs, id, mode) => ({ ...prefs, modes: { ...prefs.modes, [id]: mode } });

/** The pin toggle: pinned goes back to Auto, anything else is pinned. */
export const withPinToggled = (prefs, id) => withMode(prefs, id, prefs.modes[id] === 'pinned' ? 'auto' : 'pinned');

// ---- what the project needs (smart defaults) ----------------------------------------------------------------------

const DB_IMAGE = /(postgres|mongo|redis|mysql|mariadb|neo4j|minio|sqlite)/i;

/**
 * What the page already knows about the project, for the smart defaults: `{ services, datastores, apiServices }`.
 * `services`: `[key, service]` entries of the configuration (the real ones); `technologies`: `.mia/project.json`'s list.
 * The `services` count is every real service the project Home lists, a datastore service included (a request from the
 * app to its database is a trace across two services too): Traces arrives at 2, as More says.
 * A datastore: a datastore technology, a service that runs one, or a store the sandbox runs for the project (`stores`:
 * how many the vfs-service Data overview lists, hooks/useDataStoresSignal.js). The last is the one that counts for a
 * project made by the scaffolds: vfs_scaffold_db writes `services/<app>/db/init/*.sql` and the sandbox provisions
 * PostgreSQL for it, with no service of its own in the configuration and no technology in `.mia/project.json`.
 * A service with routes: a backend or a gateway (by its name and the project's technologies,
 * utils/projectOverview.js), since the endpoints are only read in API.
 */
export function tabSignalsOf(services = [], technologies = [], { stores = 0 } = {}) {
  const real = services.filter(([, s]) => s && s.name !== '.' && !isBrainPath(s.name) && s.type !== 'custom-resource');
  const dbServices = real.filter(([key, s]) => DB_IMAGE.test(`${key} ${s.dockerImage || ''}`));
  const apps = real.filter((entry) => !dbServices.includes(entry));
  return {
    services: real.length,
    datastores: Math.max(dataStoresOf(technologies).length + dbServices.length, Number(stores) || 0),
    apiServices: apps.filter(([key, s]) => serviceRole(key, s, technologies) !== 'frontend').length,
  };
}

/**
 * Why each advanced tab would show by itself ("you have a datastore", "3 services"), or null. `qualityBlocked`: the
 * release-gate rules that fail (hooks/useQualityGate.js), 0 when the gate passes or is unknown.
 */
export function autoReasons(signals = {}) {
  return {
    apps: null,
    files: null,
    code: null,
    api: signals.apiServices > 0 ? 'a service exposes routes' : null,
    mcp: signals.mcpServers > 0 ? 'a service exposes an MCP server' : null,
    data: signals.datastores > 0 ? 'you have a datastore' : null,
    // A trace shows the app's database calls too, so one app with a datastore has something to follow.
    traces: signals.services >= 2 ? `${signals.services} services` : signals.datastores > 0 ? 'your app calls a datastore' : null,
    quality: signals.qualityBlocked > 0 ? 'a check blocks Publish' : null,
    config: signals.secretsMissing > 0 ? 'a secret is still to set' : null,
    logs: null,
    jobs: null,
    finops: null,
    mail: null,
    terminal: null,
    git: null,
  };
}

/** The count each advanced tab carries (on the row and in More): Quality's failing gate rules. */
export function tabCounts(signals = {}) {
  return { quality: signals.qualityBlocked > 0 ? signals.qualityBlocked : 0 };
}

/** For each advanced tab: 'pinned', 'auto' (on the row by itself) or null (only in More, or hidden by the plan). */
export function shownAdvanced(prefs, signals = {}, hidden = []) {
  const reasons = autoReasons(signals);
  return Object.fromEntries(ADVANCED_IDS.map((id) => {
    if (isTabHidden(hidden, id)) { return [id, null]; }
    const mode = prefs.modes[id];
    if (mode === 'pinned') { return [id, 'pinned']; }
    if (mode === 'auto' && prefs.smart && reasons[id]) { return [id, 'auto']; }
    return [id, null];
  }));
}

/** The one-time note of an auto tab that just arrived. */
export function arrivalNote(id, signals = {}) {
  if (id === 'data') { return { title: 'Data is now a tab', text: 'Your project has a datastore. Look at and edit its records here.' }; }
  if (id === 'traces') {
    if ((signals.services || 0) < 2 && signals.datastores > 0) {
      return { title: 'Traces is now a tab', text: 'Your app uses a datastore. Follow one request into it, with its database calls and timings.' };
    }
    return { title: 'Traces is now a tab', text: `Your project has ${signals.services || 2} services. Follow one request across them, with timings.` };
  }
  if (id === 'quality') { return { title: 'Quality is now a tab', text: 'A check blocks Publish. See what to fix here.' }; }
  if (id === 'config') { return { title: 'Configuration is now a tab', text: 'A service needs a secret that is not set yet. Set it here.' }; }
  if (id === 'mcp') { return { title: 'MCP is now a tab', text: 'A service exposes an MCP server. List its tools and call them here, like MCP Inspector.' }; }
  if (id === 'mail') { return { title: 'External Services is now a tab', text: 'Read the emails your app sent.' }; }
  if (id === 'terminal') { return { title: 'Terminal is now a tab', text: 'Run commands in your sandbox, in the project\'s folder.' }; }
  if (id === 'code') { return { title: 'Code is now a tab', text: 'Look at and edit the files of your services here.' }; }
  if (id === 'logs') { return { title: 'Logs is now a tab', text: 'Read each service\'s build and running output here.' }; }
  if (id === 'jobs') { return { title: 'Jobs is now a tab', text: 'See and run the scheduled jobs of every service here.' }; }
  if (id === 'finops') { return { title: 'FinOps is now a tab', text: 'See what this project costs in Flow and estimate it in production.' }; }
  if (id === 'git') { return { title: 'Git is now a tab', text: 'Commit, push and pull each service\'s repository here.' }; }
  return { title: 'API is now a tab', text: 'A service exposes routes. Try its endpoints and send a test request here.' };
}

/** The auto tabs this person was already told about (once each, in every project). */
export function readArrived() {
  try {
    const list = JSON.parse(localStorage.getItem(ARRIVED_KEY) || '[]');
    return Array.isArray(list) ? list.filter((id) => ADVANCED_IDS.includes(id)) : [];
  } catch {
    return [];
  }
}

export function markArrived(id) {
  const before = readArrived();
  const next = [...new Set([...before, id])];
  try { localStorage.setItem(ARRIVED_KEY, JSON.stringify(next)); } catch { /* told again next time */ }
  if (next.length !== before.length) { stored(); }
  return next;
}

// ---- the row ------------------------------------------------------------------------------------------------------

/**
 * The tab row: `{ build, history }`. `build`: Home · Preview, the advanced tabs on the row (pinned or auto, in
 * their order, `dot` while an auto one is new, `count` Quality's failing rules), then the temporary one. `history`:
 * History, always one tab (a stored `split` is ignored). `hidden`: the plan's hidden tabs (and Terminal where it is
 * not offered), left out everywhere (Preview, History too).
 */
export function buildTabRow({ prefs = DEFAULT_TAB_PREFS, signals = {}, temp = null, arrived = [], hidden = [] } = {}) {
  const shown = shownAdvanced(prefs, signals, hidden);
  const counts = tabCounts(signals);
  const advanced = (tab) => ({
    ...tab, kind: shown[tab.id], dot: shown[tab.id] === 'auto' && !arrived.includes(tab.id), count: counts[tab.id] || 0,
  });
  const building = BUILD_TABS.filter((tab) => !isTabHidden(hidden, tab.id)).map((tab) => ({ ...tab, kind: 'build' }));
  // Home, then the leading advanced tabs (Apps, Files) when pinned, then Preview and the rest.
  const build = [
    ...building.slice(0, 1),
    ...ADVANCED_TABS.filter((tab) => tab.lead && shown[tab.id]).map(advanced),
    ...building.slice(1),
    ...ADVANCED_TABS.filter((tab) => !tab.lead && shown[tab.id]).map(advanced),
  ];
  const tempTab = temp && !shown[temp] && !isTabHidden(hidden, temp) ? ADVANCED_TABS.find((tab) => tab.id === temp) : null;
  if (tempTab) { build.push({ ...tempTab, kind: 'temp', count: counts[tempTab.id] || 0 }); }
  const history = isTabHidden(hidden, 'history') ? [] : [{ id: 'history', label: 'History', kind: 'history' }];
  return { build, history };
}

/**
 * The narrow-width fold: which tabs move into "More +N". `items` in row order (`{ id, foldable }`), `widths` by id,
 * `available` the row's width, `reserve` what More and the divider take. The rightmost foldable tabs go first; the
 * active one never does. An unknown width (0, nothing measured) folds nothing.
 */
export function foldTabs(items, { widths = {}, available = 0, reserve = 0, active = null, fallbackWidth = 90 } = {}) {
  if (!available || available <= 0) { return []; }
  const width = (id) => widths[id] || fallbackWidth;
  let total = items.reduce((sum, item) => sum + width(item.id), 0) + reserve;
  if (total <= available) { return []; }
  const folded = [];
  for (let i = items.length - 1; i >= 0 && total > available; i -= 1) {
    const item = items[i];
    if (item.foldable && item.id !== active) {
      folded.unshift(item.id);
      total -= width(item.id);
    }
  }
  return folded;
}

// ---- History's sub-views ------------------------------------------------------------------------------------------

/** The History view last used in this browser: 'changes' (default), 'activity' or 'sessions'. */
export function lastHistorySub() {
  try {
    const saved = localStorage.getItem(HISTORY_KEY);
    return HISTORY_IDS.includes(saved) ? saved : 'changes';
  } catch {
    return 'changes';
  }
}

export function rememberHistorySub(sub) {
  if (!HISTORY_IDS.includes(sub) || sub === lastHistorySub()) { return; }
  try { localStorage.setItem(HISTORY_KEY, sub); } catch { /* not remembered */ }
  stored();
}

// ---- all of it at once (the server's `tabs`) ----------------------------------------------------------------------

/** Everything this browser keeps, in the shape of the person's settings' `tabs` (vfs-service). */
export function readLocalTabs() {
  return { ...readTabPrefs(), arrived: readArrived(), historyView: lastHistorySub() };
}

/** The server's `tabs` cached in this browser, without sending them back; every tab row is told (TAB_PREFS_EVENT). */
export function cacheServerTabs(tabs = {}) {
  const arrived = Array.isArray(tabs.arrived) ? tabs.arrived.filter((id) => ADVANCED_IDS.includes(id)) : [];
  try {
    localStorage.setItem(ARRIVED_KEY, JSON.stringify(arrived));
    if (HISTORY_IDS.includes(tabs.historyView)) { localStorage.setItem(HISTORY_KEY, tabs.historyView); }
  } catch { /* kept for this page only */ }
  // A tab the server's settings do not know yet (vfs-service personSettings keeps a fixed list of modes, without Logs,
  // Jobs or FinOps) keeps the mode chosen in this browser instead of going back to its default.
  const local = readTabPrefs().modes;
  const known = tabs.modes && typeof tabs.modes === 'object' ? tabs.modes : {};
  const modes = Object.fromEntries(ADVANCED_IDS.map((id) => [id, isMode(known[id]) ? known[id] : local[id]]));
  return saveTabPrefs({ ...tabs, modes }, { silent: true });
}
